import { defineStore } from 'pinia'
import type { KnowledgeBaseArticle } from '@/types/knowledgeBaseType'
import KnowledgeBaseService from '@/services/KnowledgeBaseService'
import { isSupplier } from '@/utils/isAuthorized'

export const useKnowledgeBaseStore = defineStore('knowledgeBase', {
  state: (): {
    articles: KnowledgeBaseArticle[]
    tags: Set<string>
  } => ({
    articles: [],
    tags: new Set(),
  }),
  actions: {
    async clearKnowledgeBase() {
      // This function is triggered on every route change in `@/router/index.ts`
      const oldTags = this.tags

      this.tags = new Set()

      this.tags.add(isSupplier() ? 'gebruiker:aanbieder' : 'gebruiker:gemeente')

      const oldAndNewAreEqual = (): boolean =>
        oldTags.size === this.tags.size &&
        [...oldTags].every((x) => this.tags.has(x))

      if (oldAndNewAreEqual()) return

      this.articles = []

      await this.fetchArticles()
    },

    async fetchArticles() {
      const { data } = await KnowledgeBaseService.getArticles(this.tagsAsArray)

      this.articles = data.data.sort((a, b) =>
        a.tags.length > b.tags.length
          ? -1
          : a.tags.length < b.tags.length
            ? 1
            : 0,
      )

      return this.articles
    },

    async setTags(...tags: Array<string | string[]>) {
      this.tags = new Set(tags.flat())
      this.tags.add(isSupplier() ? 'gebruiker:aanbieder' : 'gebruiker:gemeente')

      await this.fetchArticles()
    },

    async addTags(...tags: Array<string | string[]>) {
      tags.flat().forEach((tag) => this.tags.add(tag))

      await this.fetchArticles()
    },

    async removeTags(...tags: Array<string | string[]>) {
      tags.flat().forEach((tag) => this.tags.delete(tag))
      this.tags.add(isSupplier() ? 'gebruiker:aanbieder' : 'gebruiker:gemeente')

      await this.fetchArticles()
    },
  },
  getters: {
    tagsAsArray: (state) => Array.from<string>(state.tags),
  },
})
